import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

const STRIPE_PAYOUTS_COUNTRIES = ["VN", "US", "GB", "MY", "TH", "HK", "AE", "PH", "ID", "SG", "IN", "AU", "CA", "MX", "BR"];

export function WireOutQueueTile() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState(null);
  const [busy, setBusy] = useState({});
  const [revealed, setRevealed] = useState({});

  const load = useCallback(() => {
    api.get("/admin/wire-out-queue")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    api.get("/admin/recently-wired")
      .then((r) => setRecent(r.data))
      .catch(() => setRecent(null));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markWired = async (id, refNum) => {
    const confirmed = window.confirm(
      `⚠️ Before clicking OK, confirm you have ALREADY initiated the wire transfer in your bank portal.\n\n` +
      `This button does NOT send money. It only records that you manually wired the funds from YOUR bank (Chase, BoA, etc.) to the receiver's bank account OUTSIDE of Zcardon.\n\n` +
      `Deposit: ${refNum}\n\n` +
      `Have you already logged into your bank and initiated the wire to the receiver?`,
    );
    if (!confirmed) return;
    const bankRef = window.prompt(
      `Enter your bank's wire confirmation number for ${refNum}.\n\nThis is REQUIRED for compliance + dispute resolution — your bank gives this to you right after initiating the wire (e.g. "W-3428-9912" or similar).`,
      "",
    );
    if (bankRef === null) return;
    if (!bankRef.trim()) {
      toast.error("Wire confirmation number is required — copy it from your bank's wire-transfer confirmation.");
      return;
    }
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.post(`/admin/direct-deposits/${id}/mark-wired`, { bank_reference: bankRef.trim() });
      if (r.data?.customer_notified) {
        toast.success(`Marked wired · customer emailed at ${r.data.customer_email}`);
      } else {
        toast.success("Marked wired (no customer email on file)");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark wired");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  const unmarkWired = async (id, refNum) => {
    if (!window.confirm(
      `Revert ${refNum} back to the Wire-Out Queue?\n\nUse this if you marked a deposit as wired by mistake (i.e. you clicked the button but never actually initiated the wire in your bank). This will return the deposit to "paid_to_zcardon" so you can retry the wire.`,
    )) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/direct-deposits/${id}/unmark-wired`);
      toast.success(`Reverted · ${refNum} back in queue`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  const copy = (text, label) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  };

  if (!data) return null;
  const isEmpty = !data.count;
  const hasOverdue = (data.overdue_count || 0) > 0;

  return (
    <div
      className={`rounded-xl border p-5 ${
        hasOverdue ? "border-rose-300 bg-rose-50/40 ring-1 ring-rose-200"
        : !isEmpty ? "border-amber-300 bg-amber-50/40"
        : "border-[#E5E0D8] bg-white"
      }`}
      data-testid="wire-out-queue-tile"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${
                hasOverdue ? "bg-rose-500 animate-pulse"
                : !isEmpty ? "bg-amber-500"
                : "bg-emerald-500"
              }`}
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-[#112318]">Wire-out queue</p>
            {!isEmpty && (
              <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${
                hasOverdue ? "bg-rose-100 text-rose-700 border-rose-200"
                : "bg-amber-100 text-amber-700 border-amber-200"
              }`} data-testid="wire-out-queue-count">
                {data.count} pending{hasOverdue ? ` · ${data.overdue_count} overdue` : ""}
              </span>
            )}
            {isEmpty && (
              <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                all clear
              </span>
            )}
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            Customers paid via Stripe — funds are in your Zcardon Stripe balance and need to be wired to the receiver's bank from your business banking. {!isEmpty && (<span className="font-medium text-[#112318]">Pending: ${data.total_usd_pending?.toFixed(2)}</span>)}
          </p>
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900 leading-relaxed" data-testid="wire-out-manual-warning">
            <b>⚠️ "I've wired this" does NOT send money.</b> It only logs that you manually initiated the wire in your bank's portal (Chase, BoA, Wise, etc.). Always run the actual wire in your bank FIRST, then click the button to record the wire confirmation number.
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <BulkStripePayoutsButton items={data.items} onDone={load} />
          <button
            onClick={load}
            className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5"
            data-testid="wire-out-queue-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {!isEmpty && (
        <div className="mt-4 space-y-2" data-testid="wire-out-queue-list">
          {data.items.map((d) => {
            const overdue = d.overdue;
            const showFull = !!revealed[d.id];
            return (
              <div
                key={d.id}
                className={`rounded-lg border p-3 bg-white ${overdue ? "border-rose-200" : "border-[#E5E0D8]"}`}
                data-testid={`wire-out-row-${d.reference_number}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-semibold text-[#112318]">${Number(d.amount).toFixed(2)} {d.currency}</span>
                  <span className="text-xs text-[#4A5C50]">→ {d.receiver_name}</span>
                  <span className="mono text-[10px] text-[#4A5C50]">{d.reference_number}</span>
                  <span className={`ml-auto text-[10px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded ${
                    overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    {d.hours_since_paid < 1 ? `${Math.round(d.hours_since_paid * 60)}m` : `${d.hours_since_paid.toFixed(0)}h`} since paid
                  </span>
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px] mono">
                  <span className="text-[#4A5C50]">Bank:</span><span className="text-[#112318] truncate">{d.bank_name}</span>
                  <span className="text-[#4A5C50]">Account:</span>
                  <span className="text-[#112318] flex items-center gap-2">
                    {showFull ? d.account_number : `••••${(d.account_number || "").slice(-4)}`}
                    <button onClick={() => setRevealed(r => ({...r, [d.id]: !showFull}))} className="text-[10px] underline text-[#184A30]/70 hover:text-[#184A30]" data-testid={`reveal-acct-${d.reference_number}`}>
                      {showFull ? "Hide" : "Reveal"}
                    </button>
                    {showFull && (
                      <button onClick={() => copy(d.account_number, "Account #")} className="text-[10px] underline text-[#184A30]/70 hover:text-[#184A30]">Copy</button>
                    )}
                  </span>
                  {d.routing_number && (<><span className="text-[#4A5C50]">Routing:</span><span className="text-[#112318] flex items-center gap-2">{d.routing_number}<button onClick={() => copy(d.routing_number, "Routing #")} className="text-[10px] underline text-[#184A30]/70">Copy</button></span></>)}
                  {d.swift_code && (<><span className="text-[#4A5C50]">SWIFT:</span><span className="text-[#112318]">{d.swift_code}</span></>)}
                  {d.memo && (<><span className="text-[#4A5C50]">Memo:</span><span className="text-[#112318]">{d.memo}</span></>)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 justify-end items-center">
                  <StripePayoutControls deposit={d} onChanged={load} />
                  <button
                    onClick={() => markWired(d.id, d.reference_number)}
                    disabled={!!busy[d.id]}
                    className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md bg-[#184A30] text-white hover:bg-[#0F3320] disabled:opacity-50"
                    data-testid={`mark-wired-${d.reference_number}`}
                  >
                    {busy[d.id] ? "Marking…" : "✓ I've wired this"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {recent?.items?.length > 0 && (
        <details className="mt-4 border-t border-[#E5E0D8] pt-3" data-testid="recently-wired-section">
          <summary className="text-[11px] tracking-[0.2em] uppercase text-[#4A5C50] cursor-pointer hover:text-[#184A30]">
            Recently marked wired ({recent.count}) · revert if you didn't actually wire from your bank
          </summary>
          <div className="mt-2 space-y-1.5">
            {recent.items.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-[12px] bg-emerald-50/40 border border-emerald-100 rounded-md px-2 py-1.5" data-testid={`recently-wired-row-${r.reference_number}`}>
                <div className="min-w-0 flex-1">
                  <span className="mono text-[#112318]">{r.reference_number}</span>
                  <span className="ml-2 text-[#4A5C50]">${Number(r.amount || 0).toFixed(2)} → {r.receiver_name} · {r.bank_name}</span>
                  <span className="ml-2 text-[10px] text-[#4A5C50]">
                    {r.bank_wire_reference ? `ref ${r.bank_wire_reference}` : <span className="text-amber-700">no bank ref ⚠️</span>}
                  </span>
                </div>
                <button
                  onClick={() => unmarkWired(r.id, r.reference_number)}
                  disabled={!!busy[r.id]}
                  className="text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50 shrink-0"
                  data-testid={`unmark-wired-${r.reference_number}`}
                >
                  {busy[r.id] ? "…" : "Revert"}
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function BulkStripePayoutsButton({ items, onDone }) {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/payouts/status")
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, []);

  if (!items?.length || enabled === null) return null;

  const run = async () => {
    if (!enabled) {
      toast.error("Stripe Payouts is dormant — set GLOBAL_PAYOUTS_ENABLED=true first");
      return;
    }
    const country = window.prompt(
      `Bulk-create Stripe recipients + send onboarding links for all ${items.length} pending wire-outs?\n\n` +
      `Pick a destination country code (2 letters).\n\nSupported: ${STRIPE_PAYOUTS_COUNTRIES.join(", ")}\n\n` +
      `(Receivers in different countries should be set up individually.)`,
      "VN"
    );
    if (!country) return;
    const cc = country.trim().toUpperCase();
    if (!STRIPE_PAYOUTS_COUNTRIES.includes(cc)) {
      toast.error(`${cc} not supported`);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/payouts/bulk-setup", {
        deposit_ids: items.map((d) => d.id),
        country: cc,
        send_onboarding_link: true,
        channel: "both",
      });
      setResult(r.data);
      const msg = `Bulk: ${r.data.succeeded} created · ${r.data.skipped} skipped · ${r.data.failed} failed · ${r.data.links_sent} links sent`;
      if (r.data.failed > 0) toast.warning(msg);
      else toast.success(msg);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk setup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={busy || !enabled}
        className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-[#CBA35C]/60 text-[#7B5418] hover:bg-[#CBA35C]/10 disabled:opacity-40"
        data-testid="bulk-stripe-payouts-btn"
        title={enabled ? "Bulk-create Stripe recipients + send onboarding links" : "Feature dormant — set GLOBAL_PAYOUTS_ENABLED=true"}
      >
        {busy ? "…" : enabled ? `↗ Bulk Stripe (${items.length})` : "Bulk Stripe (dormant)"}
      </button>
      {result && (
        <div className="absolute mt-12 right-0 z-10 bg-white border border-stone-200 rounded-lg shadow-lg p-3 max-w-md text-[11px]" data-testid="bulk-stripe-results">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="font-semibold">Bulk run complete</p>
            <button onClick={() => setResult(null)} className="text-stone-400 hover:text-stone-700">×</button>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <span className="text-emerald-700">✓ {result.succeeded}</span>
            <span className="text-stone-500">⊘ {result.skipped} skip</span>
            <span className="text-rose-700">✗ {result.failed}</span>
            <span className="text-amber-700">📨 {result.links_sent}</span>
          </div>
          {result.results?.filter((r) => !r.ok).slice(0, 5).map((r) => (
            <p key={r.deposit_id} className="text-rose-800 truncate">
              ✗ {r.reference_number || r.deposit_id?.slice(0, 8)}: {r.error}
            </p>
          ))}
        </div>
      )}
    </>
  );
}

function StripePayoutControls({ deposit, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/admin/payouts/recipients/by-deposit/${deposit.id}`)
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, [deposit.id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!state) return null;

  const enabled = state.feature_enabled;
  const rec = state.recipient;
  const payout = state.payout;

  if (payout) {
    const tone =
      payout.status === "paid" ? "bg-emerald-100 text-emerald-700"
      : payout.status === "failed" || payout.status === "returned" ? "bg-rose-100 text-rose-700"
      : "bg-amber-100 text-amber-700";
    return (
      <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 rounded ${tone}`} data-testid={`stripe-payout-status-${deposit.reference_number}`}>
        Stripe payout · {payout.status || "pending"}
      </span>
    );
  }

  if (rec && rec.payout_method_id) {
    const initiate = async () => {
      if (!window.confirm(`Initiate Stripe Global Payout for ${deposit.reference_number}?\n\nAmount: ${deposit.currency} ${Number(deposit.amount).toFixed(2)}\nDestination: ${rec.country} bank ending …${(rec.payout_method_id || "").slice(-4)}\n\nThis is irreversible once Stripe accepts the OutboundPayment.`)) return;
      setBusy(true);
      try {
        await api.post(`/admin/payouts/initiate`, {
          deposit_id: deposit.id,
          recipient_id: rec.id,
          amount: Number(deposit.amount),
          currency: (deposit.currency || "USD").toUpperCase(),
        });
        toast.success("Outbound payout created");
        refresh();
        onChanged && onChanged();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Initiate failed");
      } finally {
        setBusy(false);
      }
    };
    return (
      <button
        onClick={initiate}
        disabled={busy || !enabled}
        className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-[#CBA35C] text-[#7B5418] hover:bg-[#CBA35C]/10 disabled:opacity-40"
        data-testid={`stripe-initiate-${deposit.reference_number}`}
        title={enabled ? "" : "Feature dormant — set GLOBAL_PAYOUTS_ENABLED=true"}
      >
        {busy ? "…" : `→ Send via Stripe (${rec.country})`}
      </button>
    );
  }

  if (rec) {
    const sendLink = async () => {
      setBusy(true);
      try {
        const r = await api.post(`/admin/payouts/recipients/${rec.id}/send-onboarding-link`, { channel: "both" });
        toast.success(`Link sent · email:${r.data.email_sent ? "✓" : "✗"} sms:${r.data.sms_sent ? "✓" : "✗"}`);
        if (r.data?.onboarding_url) {
          if (navigator.clipboard) navigator.clipboard.writeText(r.data.onboarding_url).catch(() => {});
        }
        refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not send link");
      } finally {
        setBusy(false);
      }
    };
    return (
      <button
        onClick={sendLink}
        disabled={busy || !enabled}
        className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-[#CBA35C] text-[#7B5418] hover:bg-[#CBA35C]/10 disabled:opacity-40"
        data-testid={`stripe-send-link-${deposit.reference_number}`}
        title={enabled ? "" : "Feature dormant — set GLOBAL_PAYOUTS_ENABLED=true"}
      >
        {busy ? "…" : `Send Stripe link (${rec.country})`}
      </button>
    );
  }

  const setup = async () => {
    const country = window.prompt(
      `Set up Stripe Global Payouts for ${deposit.receiver_name}?\n\nEnter destination country code (2 letters, e.g. VN, US, GB).\n\nSupported: ${STRIPE_PAYOUTS_COUNTRIES.join(", ")}`,
      "VN"
    );
    if (!country) return;
    const cc = country.trim().toUpperCase();
    if (!STRIPE_PAYOUTS_COUNTRIES.includes(cc)) {
      toast.error(`${cc} not supported. Pick one of: ${STRIPE_PAYOUTS_COUNTRIES.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/payouts/recipients/from-deposit`, { deposit_id: deposit.id, country: cc });
      toast.success(`Stripe recipient created (${cc}) · next: send onboarding link`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create recipient");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={setup}
      disabled={busy || !enabled}
      className="text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-40"
      data-testid={`stripe-setup-${deposit.reference_number}`}
      title={enabled ? "Use Stripe Global Payouts to send funds directly to the receiver's bank" : "Feature dormant — set GLOBAL_PAYOUTS_ENABLED=true"}
    >
      {busy ? "…" : enabled ? "Set up Stripe Payouts" : "Stripe Payouts (dormant)"}
    </button>
  );
}
